import React from 'react'
import { Paper } from '@e3dc-react/shell/Elements/Paper'
import { Box } from '@mui/material'
import { Components } from 'src/Libs/shop/models'
import AdditionalComponentProductCard from './AdditionalComponentProductCard'
import { J1LeftarrowA, J1RightarrowA } from '@e3dc-react/icons'
import { makeStyles } from 'tss-react/mui'
import { useShopHorizontalScroll } from 'src/Libs/shop/hooks'

interface SelectedConfiguredProduct {
  id: string
  qty: number
}

interface AdditionalComponentCategoryCardProps {
  categoryData: Components
  onMoreInfo: (productId: string) => void
  isProductSelected: (productId: string) => boolean
  getSelectedProduct: (productId: string) => SelectedConfiguredProduct | undefined
  onDecreaseProductQuantity: (categoryId: string, selectedProductId: string, selectedProductQty: number) => void
  onIncreaseProductQuantity: (categoryId: string, selectedProductId: string) => void
  onAddProductToCart: (categoryId: string, productId: string, minPurchase: number, productHint: string | false) => void
  onRemoveProductFromCart: (categoryId: string, productId: string) => void
}

const useStyles = makeStyles()(theme => ({
  gridContainer: {
    display: 'flex',
    overflowX: 'auto',
    width: '100%',
    minWidth: '30px',
    flexWrap: 'nowrap',
    marginLeft: '12px',
  },
  paperContent: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    paddingRight: theme.spacing(5),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    overflowX: 'hidden',
    position: 'relative',
    [theme.breakpoints.down('xs')]: {
      minWidth: '100%',
      paddingRight: 0,
    },
  },
  leftArrow: {
    position: 'absolute',
    top: '50%',
    left: 0,
    transform: 'translateY(-50%)',
    backgroundColor: theme.palette.primary.main,
    borderRadius: '50%',
    padding: theme.spacing(0),
    color: theme.palette.primary.contrastText,
    minWidth: '32px',
    minHeight: '32px',
  },
  rightArrow: {
    position: 'absolute',
    top: '50%',
    right: -20,
    transform: 'translateY(-50%)',
    backgroundColor: theme.palette.primary.main,
    borderRadius: '50%',
    padding: theme.spacing(0),
    color: theme.palette.primary.contrastText,
    minWidth: '32px',
    minHeight: '32px',
  },
}))

/*
 * Displays a product category with horizontal scroll and selectable product cards
 */
const AdditionalComponentCategoryCard: React.FC<AdditionalComponentCategoryCardProps> = ({
  categoryData,
  onMoreInfo,
  isProductSelected,
  getSelectedProduct,
  onDecreaseProductQuantity,
  onIncreaseProductQuantity,
  onAddProductToCart,
  onRemoveProductFromCart,
}) => {
  const { classes } = useStyles()
  const { containerRef, canScrollToLeft, canScrollToRight, handleScrollLeft, handleScrollRight } = useShopHorizontalScroll()

  const productsLength = categoryData.products.length

  return (
    <Paper title={`${categoryData.translated.name} (${productsLength})`} className={classes.paperContent}>
      <Box className={classes.gridContainer} ref={containerRef}>
        {categoryData.products
          .sort((a, b) => a.position - b.position)
          .map(productWrapper => {
            return (
              <AdditionalComponentProductCard
                key={productWrapper.product.id}
                productData={productWrapper.product}
                categoryId={categoryData.id}
                onMoreInfo={onMoreInfo}
                isProductSelected={isProductSelected(productWrapper.product.id)}
                selectedProduct={getSelectedProduct(productWrapper.product.id)}
                onDecreaseProductQuantity={onDecreaseProductQuantity}
                onIncreaseProductQuantity={onIncreaseProductQuantity}
                onAddProductToCart={onAddProductToCart}
                onRemoveProductFromCart={onRemoveProductFromCart}
              />
            )
          })}
      </Box>
      {canScrollToLeft && <J1LeftarrowA className={classes.leftArrow} onClick={handleScrollLeft} />}
      {canScrollToRight && <J1RightarrowA className={classes.rightArrow} onClick={handleScrollRight} />}
    </Paper>
  )
}

export default AdditionalComponentCategoryCard
